/* eslint-disable react-portal/no-react-router-link */
import { forwardRef } from 'react';
import { get, isFunction, mapValues, merge, noop, omit, pick, pickBy, extend, includes, template } from 'lodash';
import { bool, func, node, object, oneOfType, shape, string } from 'prop-types';
import { Link as ReactRouterLink, useHref, useResolvedPath } from 'react-router-dom';

import { encodeState, getUrlParamFromEncodedState } from '../../utilities/FilterStateUrlUtilities';
import { getUrlParamFromState } from '../../utilities/PageStateUrlUtilities';
import useSideNavState from '../../stores/SideNavStateStore';
import useIsPrinting from '../../utilities/PrintUtilities';
import { ADMIN_HOME, ReactAdminPages } from '../pages-admin/AdminPages';
import { usePlmPath, PLM_HOME } from '../../utilities/PgmUtilities';

const partialPathType = shape({
    pathname: string,
    search: string,
    hash: string,
});

const reactPaths = [
    'logout',
    'new-dashboard',
    'sites-overview',
    'sites',
    'site-detail',
    'business-services-overview',
    'business-services',
    'business-service-detail',
    'agents',
    'agent-detail',
    'tests',
    'test-detail',
    'test-log',
    'path-detail',
    'reports',
    'alerts',
    'servers',
    'server-detail',
    'network-devices',
    'device-detail',
    'interfaces',
    'interface-detail',
    'wireless-controllers',
    'wireless-controller-detail',
    'access-points',
    'access-point-detail',
    'radios',
    'radio-detail',
    'vmware-hosts',
    'vmware-host-detail',
    'vmware-vms',
    'vmware-vm-detail',
    'usersProfile',
    'usersPassword',
    'license',
    'net-test-now',
    'quickTests',
];

function makeLink(linkCfg, data) {
    // The linkCfg parameter can be a function that returns the link configuration.
    return isFunction(linkCfg) ? linkCfg(data) : linkCfg;
}

export function buildLinkUrl(linkCfg, data, filterState, pageState) {
    const link = makeLink(linkCfg, data);

    if (!link) return 'Error: no link configuration';

    // Build the target page state.
    const {
        filterState: linkFilterState,
        pageState: linkPageState = {},
        pageStateParams = [],
        staticFilterParams = {},
        mappedFilterParams,
        mappedTargetParams = {},
        excludedFilterParams = [],
        target,
    } = link;

    const picker = isFunction(pageStateParams) ? pickBy : pick;
    const targetPageState = picker(merge({}, pageState?.values || {}, linkPageState), pageStateParams);

    const pageStateParam = getUrlParamFromState(targetPageState);
    const encodedFilterState = linkFilterState?.encoded || filterState?.encoded || {};

    // Build the target filter state.
    const mappedFilterState = encodeState(
        extend(
            mapValues(mappedFilterParams, (mapping) => {
                if (isFunction(mapping)) return mapping(data);
                return get(data, mapping);
            }),
            staticFilterParams
        )
    );

    const targetParams = mapValues(mappedTargetParams, (mapping) => get(data, mapping));

    const targetFilterState = omit(
        { ...encodedFilterState, ...mappedFilterState, ...targetParams },
        excludedFilterParams
    );

    // A target URL param would be "userId" for the URL: /admin/users/:userId
    const targetWithUrlVars = target
        .split(':')
        .map((param, index) => {
            if (index === 0) return param;

            const paramName = param.match(/^[a-z0-9_.]+/gi)[0];
            return param.replace(paramName, get(data, paramName, ''));
        })
        .join('');

    const urlParams = getUrlParamFromEncodedState(targetFilterState, pageStateParam);
    return `/${targetWithUrlVars}${urlParams ? `?${urlParams}` : ''}`;
}

export function buildLinkTooltip(linkCfg, data) {
    const link = makeLink(linkCfg, data);

    if (!link) return 'Error: no link configuration';

    if (link.tooltip) return link.tooltip;

    if (link.tooltipTemplate) return template(link.tooltipTemplate)(data);

    return '';
}

function isReactPath(path) {
    const usersHome = 'users';
    const dashboardPathRegExp = /[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}/i;

    const [mainUrl] = path.split('?'); // break apart /admin/users?something=true
    const [, lvl1, lvl2, lvl3, lvl4] = mainUrl.split('/'); // now we get ["", "admin", ""]. If there is a third item, make sure there isn't a values

    // The users check is temporary until change password is done
    if (lvl1 === ADMIN_HOME) {
        return !lvl2 || includes(ReactAdminPages, lvl2);
    }

    // reset password pages
    if (lvl1 === 'reset' || lvl1 === 'login') {
        return true;
    }

    if (lvl1 === PLM_HOME) {
        if (lvl3 === ADMIN_HOME) {
            return !lvl4 || includes(ReactAdminPages, lvl4);
        }

        return includes(reactPaths, lvl3) || dashboardPathRegExp.test(lvl3) || !lvl3;
    }

    // Temporary until we move over the reset password function
    if (lvl1 === usersHome) {
        return !lvl2 || includes(reactPaths, lvl2);
    }

    return includes(reactPaths, lvl1) || dashboardPathRegExp.test(lvl1) || !lvl1;
}

/**
 * @typedef {{
 *      to?: string,
 *      plmIdDefault?: string,
 *      onClick?: (event: MouseEvent) => void,
 *      children: JSX.Element | JSX.Element[]
 * }} LinkProps
 */

/**
 * This is just a wrapper around the `react-router` `Link` component. It is responsible for removing links when the application
 * is in print mode. For that to work, this component should ONLY accept the same props as its `react-router` counterpart.
 * Avoid extending this component to do anything fancy like adding filter parameters or other link manipulation.
 *
 * @param {LinkProps & React.ReactHTMLElement<HTMLAnchorElement>} props
 * @param {React.Ref<HTMLAnchorElement>} [ref]
 */
function LinkInner({ to, plmIdDefault, preventBlur = false, children, onClick, ...restProps }, ref) {
    const resolved = useResolvedPath(to);
    const path = {
        ...resolved,
        pathname: usePlmPath(resolved.pathname, plmIdDefault),
    };
    const href = useHref(path);
    const [sideNavState, sideNavStateActions] = useSideNavState();
    const isPrinting = useIsPrinting();

    function handleClick(event) {
        onClick(event);

        // If the click handler didn't prevent default on the event, and the target is an NOT React page, directly
        // set the browser's location and prevent the default link action.
        if (!event.defaultPrevented && !isReactPath(path.pathname)) {
            // Be sure the SideNav open/close state transfers correctly when it is floated.
            if (sideNavState.floating) sideNavStateActions.setOpen(false);

            // We can directly assign to window.location, since href is already prefixed.
            // eslint-disable-next-line react-portal/no-window-location-assign
            window.location.assign(href);
            event.preventDefault();
        }
    }

    function handleMouseDown(event) {
        // In some cases, a blur event can prevent link click events from firing.  This happens on some Admin pages
        // when the breadcrumb links are clicked while a form field has focus.  The `preventBlur` property can be
        // used to suppress the blur event from firing so that the link's click event is fired.
        if (preventBlur) event.preventDefault();
    }

    return isPrinting ? (
        children
    ) : (
        <ReactRouterLink onMouseDown={handleMouseDown} onClick={handleClick} to={path} ref={ref} {...restProps}>
            {children}
        </ReactRouterLink>
    );
}

const Link = forwardRef(LinkInner);
export default Link;

Link.propTypes = {
    to: oneOfType([string, partialPathType]).isRequired,
    state: object,
    replace: bool,
    title: string,
    className: string,
    children: node,
    onClick: func,
};

Link.defaultProps = {
    state: undefined,
    replace: false,
    title: undefined,
    className: undefined,
    children: null,
    onClick: noop,
};
